// Bluesky reading chrome. Semantic skeleton lifted from bskyweb's JSON-LD +
// noscript fallback (`social-app/bskyweb/templates/post.html`), upgraded to
// proper article/header/time/footer tags with Person microdata.

import type { ReadingViewData } from "@bindery/ja-core";

import { Avatar, AvatarFallback } from "@bindery/ui/components/ui/avatar";

import { ReadingBody } from "./reading-body";

function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const m = Math.round(diff / 60_000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

export function BlueskyChrome({ data }: { data: ReadingViewData }) {
  const { reading } = data;
  const authorDid = reading.author_did ?? null;
  // source_ref is the AT-URI rkey tail for bluesky posts per the ingest worker.
  const bskyUrl = authorDid
    ? `https://bsky.app/profile/${authorDid}/post/${reading.source_ref}`
    : (reading.source_uri ?? "#");

  return (
    <main className="mx-auto max-w-[36rem] px-6 py-16">
      <article
        itemScope
        itemType="https://schema.org/DiscussionForumPosting"
        className="text-ink"
      >
        <header className="flex items-center gap-3">
          <Avatar className="ring-rule size-10 ring-1">
            <AvatarFallback className="font-jp-display text-ink bg-transparent">
              {authorDid?.slice(8, 10).toUpperCase() ?? "—"}
            </AvatarFallback>
          </Avatar>
          <div
            className="flex min-w-0 flex-1 flex-col"
            itemProp="author"
            itemScope
            itemType="https://schema.org/Person"
          >
            <span
              className="font-jp-display truncate text-base leading-tight"
              itemProp="name"
            >
              {authorDid ?? "unknown"}
            </span>
            <span className="text-ink/60 text-xs tracking-wider uppercase">
              <span itemProp="alternateName">{authorDid ?? ""}</span>
            </span>
          </div>
          {reading.created_at && (
            <time
              itemProp="datePublished"
              dateTime={reading.created_at}
              className="text-ink/60 text-xs tabular-nums"
            >
              {relativeTime(reading.created_at)}
            </time>
          )}
        </header>

        <hr className="border-rule my-8 border-t" />

        <ReadingBody data={data} />

        <footer className="border-rule mt-12 border-t pt-4">
          <a
            href={bskyUrl}
            target="_blank"
            rel="noreferrer"
            className="text-ink/60 hover:text-persimmon text-xs tracking-wider uppercase"
          >
            View on Bluesky →
          </a>
        </footer>
      </article>
    </main>
  );
}
